import { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import MessageBox from './MessageBox'
import AboutUsModal from './AboutUsModal'
import { withBase } from '../config/api'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const ContactModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation()

const [hoverBtn, setHoverBtn] = useState(null)
const [pressedBtn, setPressedBtn] = useState(null)
const [isLoading, setIsLoading] = useState(false)
const [sent, setSent] = useState(false)
const [messageBox, setMessageBox] = useState(null)
const [csrfToken, setCsrfToken] = useState(null)
const [aboutOpen, setAboutOpen] = useState(false)

const [showAboutUsHint, setShowAboutUsHint] = useState(false)
const [aboutUsTooltipTimer, setAboutUsTooltipTimer] = useState(null)
const [aboutUsCursorPos, setAboutUsCursorPos] = useState({ x: 0, y: 0 })

  const [form, setForm] = useState({
    email: '',
    subject: '',
    message: ''
  })

  const emailRegex = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

useEffect(() => {
  if (!open) return

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      if (aboutOpen) return
      onClose()
    }
  }

  window.addEventListener('keydown', handleKeyDown)

  return () => {
    window.removeEventListener('keydown', handleKeyDown)
  }
}, [open, onClose, aboutOpen])

  useEffect(() => {
    if (!open) {
      setMessageBox(null)
      setIsLoading(false)
      setSent(false)
      setForm({
        email: '',
        subject: '',
        message: ''
      })
    }
  }, [open])

  useEffect(() => {
    if (!open) return

    const initCsrf = async () => {
      try {
        const res = await fetch(withBase('/'), {
          credentials: 'include'
        })

        const token = res.headers.get('x-csrf-token')
        if (token) setCsrfToken(token)
      } catch {}
    }

    initCsrf()
  }, [open])

  const updateForm = (k, v) =>
    setForm(prev => ({ ...prev, [k]: v }))

  const fromServerMessage = data =>
    data?.message || 'Error'

  const handleSubmit = async e => {
    if (e) e.preventDefault()
    if (isLoading) return

    if (sent) {
      onClose()
      return
    }

    if (!form.email.trim()) {
      setMessageBox({
        message: t('EMAIL_REQUIRED'),
        type: 'error'
      })
      return
    }

    if (!emailRegex.test(form.email.trim())) {
      setMessageBox({
        message: t('FEEDBACK_EMAIL_INVALID'),
        type: 'error'
      })
      return
    }

    if (!form.subject.trim()) {
      setMessageBox({
        message: t('CONTACT_SUBJECT_REQUIRED'),
        type: 'error'
      })
      return
    }

    if (form.subject.trim().length < 3) {
      setMessageBox({
        message: t('FEEDBACK_SUBJECT_TOO_SHORT'),
        type: 'error'
      })
      return
    }

    if (!form.message.trim()) {
      setMessageBox({
        message: t('FEEDBACK_REQUIRED'),
        type: 'error'
      })
      return
    }

    if (form.message.trim().length < 3) {
      setMessageBox({
        message: t('FEEDBACK_TOO_SHORT'),
        type: 'error'
      })
      return
    }

    if (!csrfToken) {
      setMessageBox({
        message: t('FEEDBACK_ERROR'),
        type: 'error'
      })
      return
    }

    setIsLoading(true)
    setMessageBox(null)

    try {
      const res = await fetch(withBase('/feedback/contact'), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Accept-Language': i18n.language,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(form)
      })

      const data = await res.json().catch(() => ({}))

      if (!res.ok) {
        setMessageBox({
          message: fromServerMessage(data),
          type: 'error'
        })
        setIsLoading(false)
        return
      }

      setForm({
        email: '',
        subject: '',
        message: ''
      })

      setMessageBox({
        message: t('FEEDBACK_SENT'),
        type: 'success'
      })

      setSent(true)
    } catch {
      setMessageBox({
        message: t('FEEDBACK_ERROR'),
        type: 'error'
      })
    } finally {
      setIsLoading(false)
    }
  }

  if (!open) return null

  return createPortal(
  <>
    <div
  onClick={(e) => {
    e.stopPropagation()
    onClose()
  }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        backgroundColor: 'rgba(0,0,0,0.4)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 3000
      }}
    >
<div
  onClick={(e) => e.stopPropagation()}
  style={{
    position: 'relative',
    background: '#ededed',
  padding: 20,
  border: '3px solid #E7DFEC',
  borderRadius: 12,
  boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
  width: 'clamp(320px, 30vw, 420px)'
}}
      >
<button
  type="button"
  style={{
    ...modalPrimaryButtonStyle(
      pressedBtn,
      'about-us',
      hoverBtn === 'about-us'
    ),
    position: 'absolute',
    top: 12,
    right: 12,
    width: 42,
    height: 42,
    minWidth: 42,
    padding: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
onMouseEnter={(e) => {
  setHoverBtn('about-us')

  const timer = setTimeout(() => {
    setShowAboutUsHint(true)
  }, 600)

  setAboutUsTooltipTimer(timer)
  setAboutUsCursorPos({ x: e.clientX, y: e.clientY })
}}
onMouseMove={(e) => {
  setAboutUsCursorPos({ x: e.clientX, y: e.clientY })
}}
onMouseLeave={() => {
  setHoverBtn(null)
  setPressedBtn(null)

  if (aboutUsTooltipTimer) clearTimeout(aboutUsTooltipTimer)

  setShowAboutUsHint(false)
}}
  onMouseDown={() => setPressedBtn('about-us')}
  onMouseUp={() => setPressedBtn(null)}
  onClick={() => setAboutOpen(true)}
>
  <InfoOutlinedIcon fontSize="small" />
</button>

<h3 style={{ fontSize: 28, margin: 0, marginBottom: 24 }}>
  {t('CONTACT_US')}
</h3>

        {messageBox && (
          <MessageBox
            message={messageBox.message}
            type={messageBox.type}
          />
        )}

        <form onSubmit={handleSubmit}>
          <input
            disabled={isLoading || sent}
            placeholder={t('YOUR_EMAIL')}
            value={form.email}
            onChange={e => updateForm('email', e.target.value)}
            style={{
              width: '100%',
              height: 44,
              fontSize: 15,
              padding: '0 18px',
              marginBottom: 12,
              border: '1.5px solid #bfbfbf',
              boxSizing: 'border-box'
            }}
          />

          <input
            disabled={isLoading || sent}
            placeholder={t('CONTACT_SUBJECT')}
            value={form.subject}
            onChange={e => updateForm('subject', e.target.value)}
            style={{
              width: '100%',
              height: 44,
              fontSize: 15,
              padding: '0 18px',
              marginBottom: 12,
              border: '1.5px solid #bfbfbf',
              boxSizing: 'border-box'
            }}
          />

          <textarea
            disabled={isLoading || sent}
            placeholder={t('YOUR_MESSAGE')}
            value={form.message}
            onChange={e => updateForm('message', e.target.value)}
            rows={5}
            style={{
              width: '100%',
              fontSize: 15,
              padding: 18,
              marginBottom: 16,
              border: '1.5px solid #bfbfbf',
              boxSizing: 'border-box',
              resize: 'vertical'
            }}
          />

          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="submit"
              disabled={isLoading}
              style={{
                ...modalPrimaryButtonStyle(
                  pressedBtn,
                  'contact-submit',
                  hoverBtn === 'contact-submit'
                ),
                flex: 1
              }}
              onMouseEnter={() => setHoverBtn('contact-submit')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('contact-submit')}
              onMouseUp={() => setPressedBtn(null)}
            >
              {sent
                ? t('SENT')
                : isLoading
                ? t('SENDING')
                : t('SEND')}
            </button>

            <button
              type="button"
              disabled={isLoading}
              style={{
                ...modalSecondaryButtonStyle(
                  pressedBtn,
                  'close',
                  hoverBtn === 'close'
                ),
                flex: 1
              }}
              onMouseEnter={() => setHoverBtn('close')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('close')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('CLOSE')}
            </button>
          </div>
        </form>
      </div>
    </div>

    <AboutUsModal
      open={aboutOpen}
      onClose={() => setAboutOpen(false)}
    />
{showAboutUsHint && (
  <div
    style={{
      position: 'fixed',
      top: aboutUsCursorPos.y - 60,
      left: aboutUsCursorPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#E7DFEC',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #0A303B',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      lineHeight: 1
    }}
  >
    {t('ABOUT_US')}
  </div>
)}
  </>,
  document.body
)
}

export default ContactModal